<ng-container *uiContentLoading="initialContextLoading$ | async">
    <mat-card *ngLet="loading$ | async as loading"
              class="queue-upsert-container">
        <form *ngIf="form"
              [formGroup]="form">
            <section class="form-wrap">

                <section class="form-column">

                    <mat-card-header>
                        <mat-card-title>
                            {{ (isEditMode ? 'page_queues_edit': 'page_queues_add') | translate }}
                        </mat-card-title>
                    </mat-card-header>

                    <mat-form-field *ngLet="getField(fields.name) as queueName">
                        <input [uiAutofocus]="!isEditMode"
                               [readonly]="isEditMode ? 'readonly' : null"
                               [placeholder]="'queues_modal_name' | translate"
                               [formControlName]="fields.name"
                               matInput
                               required
                               data-cy="queue-name" />
                        <mat-error *ngIf="queueName.hasError('maxlength')"
                                   [translateParams]="{ max: queueName.getError('maxlength').requiredLength }"
                                   translate>
                            queues_modal_name_max_warning
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field *ngLet="getField(fields.description) as queueDescription">
                        <input [placeholder]="'queues_modal_description' | translate"
                               [uiAutofocus]="isEditMode"
                               [formControlName]="fields.description"
                               matInput
                               data-cy="queue-description" />
                        <mat-error *ngIf="queueDescription.hasError('maxlength')"
                                   [translateParams]="{ max: queueDescription.getError('maxlength').requiredLength }"
                                   translate>
                            queues_modal_description_max_warning
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field>
                        <ui-toggle-switch [placeholder]="'queues_modal_unique_reference' | translate"
                                          [onString]="'queues_modal_yes' | translate"
                                          [offString]="'queues_modal_no' | translate"
                                          [formControlName]="fields.enforceUniqueReference"
                                          [disabled]="isEditMode"
                                          data-cy="queue-unique-reference">
                        </ui-toggle-switch>
                    </mat-form-field>

                    <ng-container *ngLet="getField(fields.acceptAutoRetry) as acceptAutoRetry">
                        <mat-form-field *ngIf="!isEditMode || !getField(fields.maxNoOfRetries).value">
                            <ui-toggle-switch [placeholder]="'queues_modal_auto_retry' | translate"
                                              [onString]="'queues_modal_yes' | translate"
                                              [offString]="'queues_modal_no' | translate"
                                              [formControlName]="fields.acceptAutoRetry"
                                              (optionChange)="onAutoRetryOptionChange($event)"
                                              data-cy="queue-auto-retry">
                            </ui-toggle-switch>
                        </mat-form-field>

                        <mat-form-field *ngLet="getField(fields.maxNoOfRetries) as maxNoOfRetries"
                                        [hidden]="!acceptAutoRetry.value">
                            <input [readonly]="isEditMode || !acceptAutoRetry.value"
                                   [placeholder]="'queues_modal_max_retries' | translate"
                                   [attr.min]="acceptAutoRetry.value ? 1 : 0"
                                   [formControlName]="fields.maxNoOfRetries"
                                   matInput
                                   autocomplete="off"
                                   type="number"
                                   max="50"
                                   data-cy="queue-retries" />
                            <mat-error *ngIf="maxNoOfRetries.hasError('range')"
                                       [translateParams]="{
                                        min: maxNoOfRetries.getError('range').min,
                                        max: maxNoOfRetries.getError('range').max
                                    }"
                                       translate>
                                input_number_range_warning
                            </mat-error>
                        </mat-form-field>
                    </ng-container>
                </section>

                <section *ngIf="isQueueSchemaDefinitionsEnabled"
                         class="form-column">
                    <mat-card-header>
                        <mat-card-title translate>
                            queue_schema_definitions_title
                        </mat-card-title>
                    </mat-card-header>

                    <mat-form-field>
                        <ui-queue-schema [placeholder]="'queues_specific_data_json' | translate"
                                         [initialFileName]="'queues_modal_schema_specific_json' | translate"
                                         [schemaName]="'specific'"
                                         [formControlName]="fields.specificDataJsonSchema"
                                         [confirmUpload]="hasQueueItems"
                                         (reading)="onReading($event)">
                        </ui-queue-schema>
                    </mat-form-field>
                    <mat-form-field>
                        <ui-queue-schema [placeholder]="'queues_output_data_json' | translate"
                                         [initialFileName]="'queues_modal_schema_output_json' | translate"
                                         [schemaName]="'output'"
                                         [formControlName]="fields.outputDataJsonSchema"
                                         [confirmUpload]="hasQueueItems"
                                         (reading)="onReading($event)">
                        </ui-queue-schema>
                    </mat-form-field>
                    <mat-form-field>
                        <ui-queue-schema [placeholder]="'queues_analytics_data_json' | translate"
                                         [initialFileName]="'queues_modal_schema_analytics_json' | translate"
                                         [schemaName]="'analytics'"
                                         [formControlName]="fields.analyticsDataJsonSchema"
                                         [confirmUpload]="hasQueueItems"
                                         (reading)="onReading($event)">
                        </ui-queue-schema>
                    </mat-form-field>
                </section>

                <section *ngIf="isQueueSlaMonitoringEnabled"
                         class="form-column">
                    <mat-card-header>
                        <mat-card-title translate>
                            queue_sla_predictions_title
                        </mat-card-title>
                    </mat-card-header>

                    <mat-slide-toggle [labelPosition]="'after'"
                                      [checked]="slaEnabled$ | async"
                                      [disabled]="!(isProcessViewGranted$ | async)"
                                      (change)="onSlaToggle($event)"
                                      class="toggle-trigger"
                                      matSuffix
                                      data-cy="queue-sla-toggle">
                        {{ 'queue_enable_sla' | translate }}
                    </mat-slide-toggle>

                    <ng-container *ngIf="(slaEnabled$ | async) && (isProcessViewGranted$ | async)">
                        <mat-form-field>
                            <ui-suggest [placeholder]="'job_modal_form_process' | translate"
                                        [defaultValue]="'job_modal_form_process_empty' | translate"
                                        [searchSourceFactory]="processSearchFactory"
                                        [displayCount]="5"
                                        [clearable]="false"
                                        [disabled]="triggerIsDefined"
                                        (selected)="onProcessSelected($event)"
                                        formControlName="releaseKey"
                                        data-cy="queue-process-select"
                                        required>
                            </ui-suggest>
                        </mat-form-field>
                        <label class="sla-info-label"
                               translate>
                            queue_sla_info_label
                        </label>

                        <ui-time-offset-picker [formControlName]="fields.slaMinutes"
                                               [daysPlaceholder]="'queue_days_placeholder' | translate"
                                               [hoursPlaceholder]="'queue_hours_placeholder' | translate"
                                               [errorsToCheck]="slaErrorsToCheck">
                        </ui-time-offset-picker>

                        <mat-slide-toggle [labelPosition]="'after'"
                                          [checked]="riskSlaEnabled$ | async"
                                          (change)="onRiskSlaToggle($event)"
                                          class="toggle-trigger"
                                          matSuffix
                                          data-cy="queue-risk-sla-toggle">
                            {{ 'queue_enable_risk_sla' | translate }}
                        </mat-slide-toggle>

                        <ng-container *ngIf="riskSlaEnabled$ | async">
                            <ui-time-offset-picker #riskSlaTimePicker
                                                   [formControlName]="fields.riskSlaMinutes"
                                                   [daysPlaceholder]="'queue_days_placeholder' | translate"
                                                   [hoursPlaceholder]="'queue_hours_placeholder' | translate"
                                                   [errorsToCheck]="slaErrorsToCheck">
                            </ui-time-offset-picker>
                            <label *ngIf="getField(fields.riskSlaMinutes).valid"
                                   class="risk-sla-info-label"
                                   translate>
                                queue_risk_sla_default_percentage_label
                            </label>
                        </ng-container>
                    </ng-container>
                </section>

                <ng-container *ngIf="isDevMode">
                    <section class="form-column">
                        <mat-card-header>
                            <mat-card-title translate>debug</mat-card-title>
                        </mat-card-header>
                        <pre>{{ form!.value | json}}</pre>
                    </section>
                </ng-container>
            </section>

            <div class="upsert-actions">
                <button [routerLink]="['/queues']"
                        [disabled]="loading"
                        type="button"
                        data-cy="queue-cancel"
                        mat-button>
                    {{ 'upsert_form_cancel' | translate }}
                </button>

                <button [disableIf]="isSubmitDisabled || (reading$ | async)"
                        [uiAuthorize]="isEditMode ? 'Queues.Edit': 'Queues.Create'"
                        [spinnerButtonLoading]="loading"
                        (click)="submit()"
                        type="submit"
                        mat-button
                        mat-raised-button
                        data-cy="queue-submit"
                        ui-spinner-button
                        color="primary">
                    {{ (isEditMode ? 'upsert_form_update' : 'upsert_form_save') | translate }}
                </button>
            </div>
        </form>
    </mat-card>
</ng-container>
